import api from './api';
import {
  VisitasAgendaFila,
  VisitasAgendaDias,
  emptyVisitasAgendaDias,
  GenerarVisitasPlanResult,
} from '../types/visitasAgenda';
import { PagedResult } from '../types/visitas';

export const DEFAULT_AGENDA_ID = 'principal';
export const DEFAULT_AGENDA_NOMBRE = 'Agenda';

export interface VisitasAgendaApiDto {
  idVisitasAgenda: number;
  nroCliente: number;
  codigoDireccion?: string | null;
  nombreDisplay?: string | null;
  lunes: boolean;
  martes: boolean;
  miercoles: boolean;
  jueves: boolean;
  viernes: boolean;
  sabado: boolean;
  domingo: boolean;
}

export interface VisitasAgendaFilaInputDto {
  nroCliente: number;
  codigoDireccion?: string | null;
  nombreDisplay?: string | null;
  lunes: boolean;
  martes: boolean;
  miercoles: boolean;
  jueves: boolean;
  viernes: boolean;
  sabado: boolean;
  domingo: boolean;
}

export interface VisitasAgendaSearchParams {
  search?: string;
  nroCliente?: number;
  pageNumber?: number;
  pageSize?: number;
}

export interface GenerarVisitasPlanRequest {
  nroVendedor: number;
  fechaDesde: string;
  fechaHasta: string;
  nombreAgenda?: string | null;
  modo?: 'preview' | 'ejecutar';
  observaciones?: string | null;
}

const apiDiasToFrontend = (d: VisitasAgendaApiDto): VisitasAgendaDias => ({
  lun: d.lunes,
  mar: d.martes,
  mier: d.miercoles,
  jue: d.jueves,
  vie: d.viernes,
  sab: d.sabado,
  dom: d.domingo,
});

export const mapApiToFila = (d: VisitasAgendaApiDto): VisitasAgendaFila => ({
  idVisitasAgenda: String(d.idVisitasAgenda),
  nroCliente: d.nroCliente,
  codigoDireccion: d.codigoDireccion ?? undefined,
  nombreDisplay: d.nombreDisplay?.trim() || `Cliente ${d.nroCliente}`,
  dias: { ...emptyVisitasAgendaDias(), ...apiDiasToFrontend(d) },
});

export const mapFilaToInput = (f: VisitasAgendaFila): VisitasAgendaFilaInputDto => ({
  nroCliente: f.nroCliente,
  codigoDireccion: f.codigoDireccion ?? null,
  nombreDisplay: f.nombreDisplay || null,
  lunes: f.dias.lun,
  martes: f.dias.mar,
  miercoles: f.dias.mier,
  jueves: f.dias.jue,
  viernes: f.dias.vie,
  sabado: f.dias.sab,
  domingo: f.dias.dom,
});

const visitasAgendaService = {
  getAgendaPage: async (params: VisitasAgendaSearchParams = {}): Promise<PagedResult<VisitasAgendaApiDto>> => {
    const response = await api.get<PagedResult<VisitasAgendaApiDto>>('/api/VisitasAgenda', {
      params: {
        pageNumber: params.pageNumber ?? 1,
        pageSize: params.pageSize ?? 200,
        search: params.search || undefined,
        nroCliente: params.nroCliente,
      },
    });
    return response.data;
  },

  fetchAllFilas: async (search?: string): Promise<VisitasAgendaFila[]> => {
    const filas: VisitasAgendaFila[] = [];
    let page = 1;
    let totalPages = 1;
    do {
      const data = await visitasAgendaService.getAgendaPage({
        search,
        pageNumber: page,
        pageSize: 200,
      });
      data.items.forEach((row) => filas.push(mapApiToFila(row)));
      totalPages = data.totalPages;
      page += 1;
    } while (page <= totalPages);
    return filas;
  },

  reemplazar: async (filas: VisitasAgendaFila[]): Promise<VisitasAgendaFila[]> => {
    const response = await api.post<PagedResult<VisitasAgendaApiDto>>('/api/VisitasAgenda/reemplazar', {
      filas: filas.map(mapFilaToInput),
    });
    return (response.data.items ?? []).map(mapApiToFila);
  },

  generarPlanes: async (payload: GenerarVisitasPlanRequest): Promise<GenerarVisitasPlanResult> => {
    const response = await api.post<GenerarVisitasPlanResult>('/api/VisitasPlan/generar', {
      nroVendedor: payload.nroVendedor,
      fechaDesde: payload.fechaDesde,
      fechaHasta: payload.fechaHasta,
      nombreAgenda: payload.nombreAgenda ?? DEFAULT_AGENDA_NOMBRE,
      modo: payload.modo ?? 'ejecutar',
      observaciones: payload.observaciones ?? null,
    });
    const data = response.data;
    return {
      ...data,
      porDia: (data.porDia ?? []).map((d) => ({
        ...d,
        fecha: typeof d.fecha === 'string' ? d.fecha.slice(0, 10) : String(d.fecha).slice(0, 10),
      })),
    };
  },

  getCandidatosAgenda: async (nroVendedor: number) => {
    const response = await api.get<import('../types/visitasAgenda').VisitasAgendaCandidato[]>(
      '/api/VisitasPlan/candidatos-agenda',
      { params: { nroVendedor } },
    );
    return response.data;
  },
};

export default visitasAgendaService;
